import { Injectable, inject, PLATFORM_ID } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { isPlatformServer } from "@angular/common";
import { map, Observable, retry, timeout } from "rxjs";
import { environment } from "../../../environments/env";
import {
  BlogHome,
  BlogListResponse,
  BlogPost,
  BlogSectionKey,
} from "../../ecommerce/landing/pages/blog/blog.models";

/** Sobre estándar del backend de Magrolabs. */
interface ApiEnvelope<T> {
  success: boolean;
  data: T;
}

/**
 * Timeouts diferenciados por entorno:
 * - Server/prerender: generous timeout + retry so a cold backend still bakes
 *   content into the static HTML (resolver blocks nav until resolved).
 * - Browser/SPA nav: short timeout, no retry, so a slow/down backend never
 *   blocks navigation (resolver falls back to null fast via catchError).
 */
const BLOG_TIMEOUT_SERVER_MS = 20000;
const BLOG_TIMEOUT_BROWSER_MS = 3000;

/**
 * Acceso al contenido del blog. Los artículos se mantienen en admin.magrolabs
 * y se exponen vía apiMagroLabs bajo `/blog`.
 *
 * Endpoints (definidos desde el front; el backend se adapta):
 *  - GET /blog/{section}/{slug}        -> BlogPost
 *  - GET /blog/home                    -> BlogHome (último + destacados + recientes)
 *  - GET /blog/posts?section=&page=&limit= -> BlogListResponse (paginado)
 */
@Injectable({ providedIn: "root" })
export class BlogService {
  private readonly http = inject(HttpClient);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly baseUrl = `${environment.apiMagroLabs}/blog`;

  /** Returns an Observable with platform-aware timeout (+ server-side retry). */
  private withTimeout<T>(source$: Observable<T>): Observable<T> {
    const onServer = isPlatformServer(this.platformId);
    return onServer
      ? source$.pipe(
          timeout(BLOG_TIMEOUT_SERVER_MS),
          retry({ count: 2, delay: 1000 }),
        )
      : source$.pipe(timeout(BLOG_TIMEOUT_BROWSER_MS));
  }

  /** Artículo completo por sección + slug. */
  getPost(
    section: BlogSectionKey | string,
    slug: string,
  ): Observable<BlogPost> {
    return this.withTimeout(
      this.http
        .get<ApiEnvelope<BlogPost>>(`${this.baseUrl}/${section}/${slug}`)
        .pipe(map((res) => res.data)),
    );
  }

  /** Home del blog: último artículo, destacados por sección y más recientes. */
  getHome(): Observable<BlogHome> {
    return this.withTimeout(
      this.http
        .get<ApiEnvelope<BlogHome>>(`${this.baseUrl}/home`)
        .pipe(map((res) => res.data)),
    );
  }

  /** Listado paginado, opcionalmente filtrado por sección. */
  getList(
    params: {
      section?: BlogSectionKey | string;
      page?: number;
      limit?: number;
    } = {},
  ): Observable<BlogListResponse> {
    let httpParams = new HttpParams();
    if (params.section) httpParams = httpParams.set("section", params.section);
    if (params.page) httpParams = httpParams.set("page", String(params.page));
    if (params.limit)
      httpParams = httpParams.set("limit", String(params.limit));

    return this.withTimeout(
      this.http
        .get<ApiEnvelope<BlogListResponse>>(`${this.baseUrl}/posts`, {
          params: httpParams,
        })
        .pipe(map((res) => res.data)),
    );
  }
}
